// @ts-nocheck
// PUSH_KEY is nothing but vapid public key
// SUBSCRIPTION API request format
// {
//   "group": "NimbusWebPush",
//   "status_type": "subscribe",
//   "browser": "chrome",
//   "subscription": { // this object is auto generated by swReg.pushManager.subscribe()
//     "endpoint": "<url>",
//     "keys": {
//       "p256dh": "<string>",
//       "auth": "<string>"
//     }
//   }
// }
import axios from 'axios';
import { useCallback } from 'react';
import utils from '../utils/utils';
import API_ENDPOINTS from '../const/ApiEndpoints';
import useProject from './Project/useProject';

const config = {
  PUSH_KEY: window.CONFIG.PUSH_KEY,
};
/**
 * Custom hook for handling push notifications.
 *
 * @param {import('history').History} history - React Router history object.
 * @param {Object} user - User object containing user information.
 * @param {string} user.userData - User data.
 * @returns {{
 *   subscribe: () => void,
 *   urlB64ToUint8Array: (base64String: string) => Uint8Array,
 *   handleNotificationClick: () => void,
 *   handleRecentlyClickedNotification: () => void,
 * }} Object containing functions for subscribing to push notifications and handling notifications.
 */
const usePushNotifications = (navigate, user) => {
  const { activateProject, project } = useProject();
  /**
   * Converts a base64 string to a Uint8Array.
   *
   * @param {string} base64String - The base64 encoded string.
   * @returns {Uint8Array} The Uint8Array representation of the base64 string.
   */
  const urlB64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String?.length % 4)) % 4);
    const base64 = (base64String + padding)
      // eslint-disable-next-line no-useless-escape
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };
  /**
   * Subscribes to push notifications.
   */
  const subscribeForWebPushNotifications = async () => {
    try {
      const serviceWorkerRegistration = await navigator.serviceWorker?.ready;
      if (!serviceWorkerRegistration) return;

      const applicationServerKey = urlB64ToUint8Array(config.PUSH_KEY);

      const subscribeUser = async (subscription) => {
        // we dont need to send user id, backend will detect it from token
        await axios.post(
          `${API_ENDPOINTS.MODULE_BASE_URL.DI}${API_ENDPOINTS.PUSH_NOTIFICATION.SUBSCRIBE}`,
          {
            group: 'NimbusWebPush',
            status_type: 'subscribe',
            browser: utils.getBrowserName(),
            subscription,
          }
        );
      };

      // we are mapping same device/subscription to multiple users
      const existingSubscription =
        await serviceWorkerRegistration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('Already subscribed to push notifications');
        await subscribeUser(existingSubscription);
      } else {
        const subscription =
          await serviceWorkerRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
          });
        console.log('Subscribed to push notifications');
        await subscribeUser(subscription);
      }
    } catch (error) {
      console.error('Failed to subscribe for push notifications:', error);
    }
  };

  const subscribeForPollingBasedPushNotifications = async () => {
    if (!('Notification' in window)) {
      console.error(
        'This browser does not support notifications. Push notifications might not work.'
      );
      return false;
    }
    if (!('serviceWorker' in navigator)) {
      console.error(
        'This browser does not support service workers. Push notifications might not work.'
      );
      return false;
    }
    if (!navigator.cookieEnabled) {
      console.error(
        'Cookies are not enabled in this browser. Push notifications might not work.'
      );
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        let storedDeviceId = await window.cookieStore.get('NIMBUS_DEVICE_ID');
        storedDeviceId = storedDeviceId?.value;
        if (storedDeviceId) {
          // api call to remove subscription
          // subscription should be removed for all the users
          console.log('Unsubscribing from push notifications');
          await axios
            .delete(
              `${API_ENDPOINTS.MODULE_BASE_URL.DI}${API_ENDPOINTS.PUSH_NOTIFICATION.POLLING_UNSUBSCRIBE}`,
              {
                data: {
                  device_id: storedDeviceId,
                },
              }
            )
            .then(() => {
              window.cookieStore.delete('NIMBUS_DEVICE_ID');
            });
        }
        return false;
      }
      const serviceWorkerRegistration = await navigator.serviceWorker?.ready;
      if (serviceWorkerRegistration) {
        // register device. we dont need to send user id, backend will detect it from token
        await axios.post(
          `${API_ENDPOINTS.MODULE_BASE_URL.DI}${API_ENDPOINTS.PUSH_NOTIFICATION.POLLIGN_SUBSCRIBE}`,
          {
            group: 'NimbusWebPush',
            status_type: 'subscribe',
            browser: utils.getBrowserName(),
            subscription: {
              device_id: await utils.getOrCreateDeviceId(),
              permission,
            },
          }
        );
      } else {
        return false;
      }
    } catch (err) {
      console.error('Error requesting notification permission:', err);
      return false;
    }
  };

  /**
   * Redirects to the appropriate page when a notification is clicked.
   *
   * @param {Object} notificationData - Data associated with the notification.
   */
  const redirectForNotification = useCallback(
    (notificationData) => {
      if (
        [
          'Create',
          'Update',
          'PublishDocument',
          'PublishPipeline',
          'FinalResults',
        ]?.includes(notificationData?.subModule) &&
        notificationData?.module === 'TaskManager'
      ) {
        navigate(
          `/task-manager/details/${notificationData?.jobId}/${notificationData?.subModule}`
        );
      } else if (
        notificationData?.module === 'PipelineManager' &&
        notificationData?.subModule === 'PipelineResults'
      ) {
        const projectFound = project?.userProjectsList?.find(
          (prj) => prj?.project_id === notificationData?.project_id
        );
        if (projectFound?.project_id) {
          if (projectFound?.project_id !== project?.activeProject?.project_id) {
            activateProject({
              project_id: projectFound?.project_id,
            });
          }
          // Redirect to pipeline list page
          setTimeout(() => {
            navigate(
              `/pipeline/${notificationData?.jobId?.pipeline_id}/execution/${notificationData?.jobId?.execution_history_id}`
            );
          }, 100);
        }
      } else if (project.userProjectsList.length) {
        const notificationProject = project.userProjectsList.find(
          (prj) => prj.project_id === notificationData.projectId
        );
        activateProject(notificationProject);
        const url = utils.getPageUrlByModuleSubModuleName(
          notificationData.module,
          notificationData.subModule
        );
        navigate({
          pathname: url,
          search: `?${new URLSearchParams(notificationData).toString()}`,
        });
        window.dispatchEvent(new Event('UPDATE_UREAD_JOB_COUNTS'));
      }
    },
    [project.userProjectsList]
  );

  const handleNotificationClick = useCallback(() => {
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        // event is a MessageEvent object
        console.log(
          `The service worker sent me a message: ${JSON.stringify(event.data)}`
        );

        document.dispatchEvent(new Event('CLOSE_MODALS'));
        // Programmatically click all backdrops
        const backdrops = document.querySelectorAll('.MuiBackdrop-root');
        backdrops.forEach((backdrop) => {
          const clickEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          backdrop.dispatchEvent(clickEvent);
        });
        redirectForNotification(event.data);
      });
    }
  }, [redirectForNotification]);
  /**
   * Retrieves recently clicked notification data and handles it.
   * This method retrives data from cookies and raise the event.
   */
  const handleRecentlyClickedNotification = useCallback(() => {
    window?.cookieStore
      ?.get('RECENTLY_CLICKED_NOTIFICATION_DATA')
      .then((res) => {
        if (res?.value && user?.userData) {
          setTimeout(() => {
            const val = JSON.parse(res.value);
            if (
              user.userData?.id === val.userId ||
              user?.userData?.uuid === val.userId
            ) {
              redirectForNotification(val);
              // delete cookie after 5 seconds
              setTimeout(() => {
                window.cookieStore.delete('RECENTLY_CLICKED_NOTIFICATION_DATA');
              }, 5000);
            }
          }, [1000]);
        }
      });
  }, [redirectForNotification]);

  return {
    subscribeForWebPushNotifications,
    urlB64ToUint8Array,
    handleNotificationClick,
    handleRecentlyClickedNotification,
    subscribeForPollingBasedPushNotifications,
  };
};

export default usePushNotifications;
